/**
 * ExtrasItemRendererNew - MVVM-Compliant Renderer
 *
 * This renderer uses ContentViewModel from the new MVVM architecture.
 * It maps view models to ExtrasItemNew components with proper data flow.
 *
 * Responsibilities:
 * - Render list of ExtrasItemNew components from contentViewModels
 * - Provide DnD props and callbacks
 * - Map content-specific descriptions
 */

import {
  DnDParentRenderer,
  getParentDnDProps,
} from '@design-system/components/dndList/DndList';
import { extrasVM } from '../Extras.page';
import { ExtrasItemNew } from './ExtrasItemNew';
import { PropsWithChildren, ReactNode } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages } from '@fortawesome/pro-regular-svg-icons';
import { convertFileSize } from '../../../../../helpers/file-utils';
import { trimMiddle } from '@client/new/helpers/string';
import { secondsToDuration } from '@client/new/helpers/time';
import { ContentViewModel } from '../viewModel/extras.viewModel.types';
import { Content } from '../model/extras.domain';

/**
 * Get content-specific description for rendering
 * This is VIEW-level logic (how to display content type-specific info)
 * We need to access the original content from contentMap for type-specific data
 */
const getContentDescription = (
  contentVM: ContentViewModel,
  content: Content,
  childrenCount: number,
): ReactNode => {
  switch (content.type) {
    case 'ContentLegacyGuide':
      return (
        <span>
          <FontAwesomeIcon icon={faImages} /> {content.value.count} slides
        </span>
      );
    case 'ContentLink':
      return content.value.url || '';
    case 'ContentSlides':
      return (
        <span>
          <FontAwesomeIcon icon={faImages} /> {childrenCount} slides
        </span>
      );
    case 'ContentPDF':
      return (
        trimMiddle(content.value.fileName || '') +
        ' • ' +
        convertFileSize(content.value.fileSize || 0)
      );
    case 'ContentVideo':
      return secondsToDuration((content.value.durationMs || 0) / 1000);
    default:
      return '';
  }
};

export const ExtrasItemRenderer: DnDParentRenderer<
  PropsWithChildren<{ id: string }>
> = ({ provided, item: { id } }) => {
  const vm = extrasVM.useValue();

  // Early return if feature flag is disabled
  if (!vm.contentViewModels) {
    return null;
  }

  // Find the content view model for this ID
  const contentVM = vm.contentViewModels.find((cvm) => cvm.id === id);

  if (!contentVM) return null;

  // Get original content for type-specific data
  const content = vm.contentMap[id];
  if (!content) return null;

  // Skip empty slides (legacy behavior)
  const children = vm.childrenMap[id];
  if (children?.length === 0 && content.type === 'ContentSlides') {
    return null;
  }

  const dndProps = getParentDnDProps(provided);

  return (
    <ExtrasItemNew
      viewModel={contentVM}
      imageURL={contentVM.imageUrl}
      description={getContentDescription(
        contentVM,
        content,
        children?.length || 0,
      )}
      dndProps={dndProps}
      onEdit={() => vm.dispatch('Edit', contentVM.id)}
    />
  );
};
